import { useMemo } from "react";
import { Button, Flex, Icon, Icons, type TableColumnType } from "@ir/ui";
import {
  MasterDataProduct,
  type IMasterDataProduct,
  type MasterDataProduct as MasterDataProductEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
  renderMasterDataStatusChip,
  renderMasterDataTextCell,
} from "./masterDataTable.shared";

export interface MasterDataProductRow extends IMasterDataProduct {
  key: string;
}

function mapProductsToRows(
  products: MasterDataProductEntity[],
): MasterDataProductRow[] {
  return products.map((entity) => ({
    ...MasterDataProduct.toJSON(entity),
    key: String(entity.productId),
  }));
}

function buildColumns(
  onEdit: (product: MasterDataProductEntity) => void,
  onViewPrices: (product: MasterDataProductEntity) => void,
  productsById: Map<number, MasterDataProductEntity>,
): TableColumnType<MasterDataProductRow>[] {
  return [
    {
      title: "Product Name",
      dataIndex: "itemName",
      key: "itemName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 260,
      fixed: "left",
    },

    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 200,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 160,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "UPC",
      dataIndex: "upcItemNumber",
      key: "upcItemNumber",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 160,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "GL Item #",
      dataIndex: "glItemNumber",
      key: "glItemNumber",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 160,
      cellRender: renderMasterDataTextCell,
    },

    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 100,
      cellRender: renderMasterDataStatusChip,
    },
    {
      title: "Actions",
      dataIndex: "productId",
      key: "actions",
      align: "center",
      fixed: "right",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 180,
      cellRender: (value: unknown) => {
        const id = Number(value);
        const product = productsById.get(id);
        if (!product) return null;
        return (
          <Flex align="center" justifyContent="center" gap={0.5}>
            <Button
              size="small"
              variant="outline"
              color="primary"
              aria-label={`View prices for ${product.itemName}`}
              onPress={() => onViewPrices(product)}
            >
              Prices
            </Button>
            <HasAnyPermission
              requiredPermissions={[Permissions.MASTER_DATA_EDIT]}
            >
              <Button
                size="small"
                variant="outline"
                color="grey"
                aria-label={`Edit ${product.itemName}`}
                onPress={() => onEdit(product)}
              >
                <Icon name={Icons.Edit} size="small" />
              </Button>
            </HasAnyPermission>
          </Flex>
        );
      },
    },
  ];
}

interface MasterDataProductsTableProps {
  products: MasterDataProductEntity[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  onEdit: (product: MasterDataProductEntity) => void;
  onViewPrices: (product: MasterDataProductEntity) => void;
}

export const MasterDataProductsTable = ({
  products,
  search,
  setSearch,
  setPage,
  onEdit,
  onViewPrices,
}: MasterDataProductsTableProps) => {
  const productsById = useMemo(() => {
    const map = new Map<number, MasterDataProductEntity>();
    for (const product of products) {
      map.set(product.productId, product);
    }
    return map;
  }, [products]);

  const columns = useMemo(
    () => buildColumns(onEdit, onViewPrices, productsById),
    [onEdit, onViewPrices, productsById],
  );
  const dataSource = useMemo(() => mapProductsToRows(products), [products]);

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="products"
      ariaLabel="Products table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
